import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { employeesAPI } from "../api/client";
import LoadingButton from "../components/LoadingButton";

const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => String(currentYear + i));

const SECONDMENT_STATUS = "Secondment (Payroll Transfer)";
const ACTIVE_SECONDMENT_COMBO = `Active,${SECONDMENT_STATUS}`;

function getStatusFilterLabel(value) {
  if (value === ACTIVE_SECONDMENT_COMBO) return "Active + Secondment";
  return value || "All Status";
}

export default function EmployeesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState(searchParams.get("year") || "");
  const [monthFilter, setMonthFilter] = useState(searchParams.get("month") || "");
  const [buFilter, setBuFilter] = useState(searchParams.get("businessUnit") || "");
  const [divisionFilter, setDivisionFilter] = useState(searchParams.get("division") || "");
  const [deptFilter, setDeptFilter] = useState(searchParams.get("department") || "");
  const [locationFilter, setLocationFilter] = useState(searchParams.get("seatingLocation") || "");
  const [companyFilter, setCompanyFilter] = useState(searchParams.get("company") || "");
  const [genderFilter, setGenderFilter] = useState(searchParams.get("gender") || "");
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState(searchParams.get("employmentType") || "");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [periodYearFilter, setPeriodYearFilter] = useState(searchParams.get("periodYear") || "");
  const [periodMonthFilter, setPeriodMonthFilter] = useState(searchParams.get("periodMonth") || "");
  const [loading, setLoading] = useState(false);

  const [filterValues, setFilterValues] = useState({ business_units: [], divisions: [], departments: [], seating_locations: [], statuses: [] });

  const [showBUDropdown, setShowBUDropdown] = useState(false);
  const [showDivisionDropdown, setShowDivisionDropdown] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const filterToolbarRef = useRef(null);

  useEffect(() => {
    const params = {};
    if (yearFilter) params.year = yearFilter;
    if (monthFilter) params.month = monthFilter;
    if (buFilter) params.businessUnit = buFilter;
    if (divisionFilter) params.division = divisionFilter;
    if (deptFilter) params.department = deptFilter;
    if (locationFilter) params.seatingLocation = locationFilter;
    if (companyFilter) params.company = companyFilter;
    if (genderFilter) params.gender = genderFilter;
    if (employmentTypeFilter) params.employmentType = employmentTypeFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (statusFilter) params.status = statusFilter;
    if (periodYearFilter) params.periodYear = periodYearFilter;
    if (periodMonthFilter) params.periodMonth = periodMonthFilter;
    setSearchParams(params, { replace: true });
  }, [yearFilter, monthFilter, buFilter, divisionFilter, deptFilter, locationFilter, companyFilter, genderFilter, employmentTypeFilter, categoryFilter, statusFilter, periodYearFilter, periodMonthFilter, setSearchParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeesAPI.getFilterValues();
        setFilterValues(res.data);
      } catch (err) {
        console.error("Failed to fetch filter values", err);
      }
    })();
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (yearFilter) params.year = yearFilter;
      if (monthFilter) params.month = monthFilter;
      if (buFilter) params.businessUnit = buFilter;
      if (divisionFilter) params.division = divisionFilter;
      if (deptFilter) params.department = deptFilter;
      if (locationFilter) params.seatingLocation = locationFilter;
      if (companyFilter) params.company = companyFilter;
      if (genderFilter) params.gender = genderFilter;
      if (employmentTypeFilter) params.employmentType = employmentTypeFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      if (periodYearFilter) params.periodYear = periodYearFilter;
      if (periodMonthFilter) params.periodMonth = periodMonthFilter;
      const res = await employeesAPI.getAll(params);
      setEmployees(res.data.employees || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to load employees", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, yearFilter, monthFilter, buFilter, divisionFilter, deptFilter, locationFilter, companyFilter, genderFilter, employmentTypeFilter, categoryFilter, statusFilter, periodYearFilter, periodMonthFilter]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const totalPages = Math.ceil(total / limit);

  const clearAll = () => {
    setSearch("");
    setYearFilter("");
    setMonthFilter("");
    setBuFilter("");
    setDivisionFilter("");
    setDeptFilter("");
    setLocationFilter("");
    setCompanyFilter("");
    setGenderFilter("");
    setEmploymentTypeFilter("");
    setCategoryFilter("");
    setStatusFilter("");
    setPeriodYearFilter("");
    setPeriodMonthFilter("");
    setPage(1);
  };

  const hasActiveFilters = Boolean(search || yearFilter || monthFilter || buFilter || divisionFilter || deptFilter || locationFilter || companyFilter || genderFilter || employmentTypeFilter || categoryFilter || statusFilter || periodYearFilter || periodMonthFilter);

  const closeAllDropdowns = () => {
    setShowBUDropdown(false);
    setShowDivisionDropdown(false);
    setShowDeptDropdown(false);
    setShowLocationDropdown(false);
    setShowStatusDropdown(false);
  };

  // Clicking anywhere outside the filter toolbar closes every open filter
  // dropdown (native <select> filters already close on outside click).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterToolbarRef.current && !filterToolbarRef.current.contains(event.target)) {
        setShowBUDropdown(false);
        setShowDivisionDropdown(false);
        setShowDeptDropdown(false);
        setShowLocationDropdown(false);
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visiblePageNumbers = Array.from(
    { length: Math.min(4, totalPages) },
    (_, index) => {
      const startPage = Math.min(Math.max(1, page - 1), Math.max(1, totalPages - 3));
      return startPage + index;
    }
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      const day = String(d.getDate()).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      return `${day} ${month} ${year}`;
    } catch { return '—'; }
  };

  return (
    <div style={styles.page}>
      <style>{css}</style>

      <div className="header">
        <div>
          <h2>Employee Management</h2>
          <p className="subtitle">Manage and track all employees ({total})</p>
        </div>
        <button type="button" className="btn outline back-btn" onClick={() => navigate("/headcount")}>
          ← Back to HC Dashboard
        </button>
      </div>

      <div className="card">
        <div className="search-header">
          <h3>All Employees ({total})</h3>
          {loading ? <p style={{ margin: 0, color: '#475569' }}>Loading employees...</p> : null}

          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search by ID, name or email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              {search && (
                <button className="clear-search-btn" onClick={() => { setSearch(''); setPage(1); }} type="button">Clear</button>
              )}
            </div>
          </div>
        </div>

        <div className="filter-toolbar" ref={filterToolbarRef}>
          <div className="filter-toolbar-left">
            <select className="hc-filter-select" value={yearFilter} onChange={(e) => { setYearFilter(e.target.value); setPage(1); }}>
              <option value="">All Years</option>
              {YEAR_OPTIONS.map(y => (<option key={y} value={y}>{y}</option>))}
            </select>
            <select className="hc-filter-select" value={monthFilter} onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}>
              {MONTH_OPTIONS.map(m => (<option key={m.value} value={m.value}>{m.label}</option>))}
            </select>
            <div className="filter-dropdown">
              <button type="button" className={`filter-trigger ${statusFilter ? 'active' : ''}`} onClick={() => { closeAllDropdowns(); setShowStatusDropdown(!showStatusDropdown); }}>
                <span>Status</span>
                <span className="filter-value">{getStatusFilterLabel(statusFilter)}</span>
                <span className="filter-arrow">↓</span>
              </button>
              {showStatusDropdown && (
                <div className="filter-menu">
                  <button className={`filter-option ${statusFilter === '' ? 'selected' : ''}`} onClick={() => { setStatusFilter(''); setShowStatusDropdown(false); setPage(1); }}>All Status</button>
                  {(() => {
                    const activeStatuses = filterValues.statuses.filter((s) => String(s).trim().toLowerCase() === 'active');
                    const inactiveStatuses = filterValues.statuses.filter((s) => String(s).trim().toLowerCase() !== 'active');
                    return (
                      <>
                        {activeStatuses.length > 0 && (
                          <>
                            <div className="filter-menu-group">Active</div>
                            {activeStatuses.map((s) => (
                              <button key={s} className={`filter-option filter-option-indent ${statusFilter === s ? 'selected' : ''}`} onClick={() => { setStatusFilter(s); setShowStatusDropdown(false); setPage(1); }}>{s}</button>
                            ))}
                          </>
                        )}
                        {inactiveStatuses.length > 0 && (
                          <>
                            <div className="filter-menu-group">Inactive</div>
                            {inactiveStatuses.map((s) => (
                              <button key={s} className={`filter-option filter-option-indent ${statusFilter === s ? 'selected' : ''}`} onClick={() => { setStatusFilter(s); setShowStatusDropdown(false); setPage(1); }}>{s}</button>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button type="button" className={`filter-trigger ${buFilter ? 'active' : ''}`} onClick={() => { closeAllDropdowns(); setShowBUDropdown(!showBUDropdown); }}>
                <span>BU</span>
                <span className="filter-value">{buFilter || 'All BU'}</span>
                <span className="filter-arrow">↓</span>
              </button>
              {showBUDropdown && (
                <div className="filter-menu">
                  <button className="filter-option" onClick={() => { setBuFilter(''); setShowBUDropdown(false); setPage(1); }}>All BU</button>
                  {filterValues.business_units.map(b => (<button key={b} className={`filter-option ${buFilter === b ? 'selected' : ''}`} onClick={() => { setBuFilter(b); setShowBUDropdown(false); setPage(1); }}>{b}</button>))}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button type="button" className={`filter-trigger ${divisionFilter ? 'active' : ''}`} onClick={() => { closeAllDropdowns(); setShowDivisionDropdown(!showDivisionDropdown); }}>
                <span>Division</span>
                <span className="filter-value">{divisionFilter || 'All Divisions'}</span>
                <span className="filter-arrow">↓</span>
              </button>
              {showDivisionDropdown && (
                <div className="filter-menu">
                  <button className="filter-option" onClick={() => { setDivisionFilter(''); setShowDivisionDropdown(false); setPage(1); }}>All Divisions</button>
                  {filterValues.divisions.map(d => (<button key={d} className={`filter-option ${divisionFilter === d ? 'selected' : ''}`} onClick={() => { setDivisionFilter(d); setShowDivisionDropdown(false); setPage(1); }}>{d}</button>))}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button type="button" className={`filter-trigger ${deptFilter ? 'active' : ''}`} onClick={() => { closeAllDropdowns(); setShowDeptDropdown(!showDeptDropdown); }}>
                <span>Department</span>
                <span className="filter-value">{deptFilter || 'All Departments'}</span>
                <span className="filter-arrow">↓</span>
              </button>
              {showDeptDropdown && (
                <div className="filter-menu">
                  <button className="filter-option" onClick={() => { setDeptFilter(''); setShowDeptDropdown(false); setPage(1); }}>All Departments</button>
                  {filterValues.departments.map(d => (<button key={d} className={`filter-option ${deptFilter === d ? 'selected' : ''}`} onClick={() => { setDeptFilter(d); setShowDeptDropdown(false); setPage(1); }}>{d}</button>))}
                </div>
              )}
            </div>

            <div className="filter-dropdown">
              <button type="button" className={`filter-trigger ${locationFilter ? 'active' : ''}`} onClick={() => { closeAllDropdowns(); setShowLocationDropdown(!showLocationDropdown); }}>
                <span>Seating Location</span>
                <span className="filter-value">{locationFilter || 'All Locations'}</span>
                <span className="filter-arrow">↓</span>
              </button>
              {showLocationDropdown && (
                <div className="filter-menu">
                  <button className="filter-option" onClick={() => { setLocationFilter(''); setShowLocationDropdown(false); setPage(1); }}>All Locations</button>
                  {filterValues.seating_locations.map(l => (<button key={l} className={`filter-option ${locationFilter === l ? 'selected' : ''}`} onClick={() => { setLocationFilter(l); setShowLocationDropdown(false); setPage(1); }}>{l}</button>))}
                </div>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <button type="button" className="btn outline filter-reset-btn" onClick={clearAll}>Clear Filters</button>
          )}
        </div>

        {employees.length === 0 && !loading ? (
          <div className="empty-state">
            <p className="empty-state-icon">Employees</p>
            <p className="empty-state-text">
              {hasActiveFilters ? 'No employees found for the selected filters. Try changing the filters.' : 'No employees found.'}
            </p>
            {hasActiveFilters && (
              <button className="btn outline" onClick={clearAll} style={{ marginTop: '16px' }}>View All Employees</button>
            )}
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Employee ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">BU</th>
                    <th scope="col">Division</th>
                    <th scope="col">Department</th>
                    <th scope="col">Status</th>
                    <th scope="col">Seating Location</th>
                    <th scope="col">Joining Date</th>
                    <th scope="col">Employment Type</th>
                    <th scope="col" align="right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.employee_id}>
                      <td className="id-cell">{emp.employee_id}</td>
                      <td className="name">
                        <div className="avatar">{(emp.name || 'E')[0].toUpperCase()}</div>
                        <div>
                          <strong>{emp.name}</strong>
                        </div>
                      </td>
                      <td>{emp.business_unit || '-'}</td>
                      <td>{emp.division || '-'}</td>
                      <td>{emp.department || '-'}</td>
                      <td><span className={`status-badge ${String(emp.status || '').trim().toLowerCase() === 'active' ? 'status-active' : emp.status ? 'status-inactive' : 'status-neutral'}`} title={emp.status || ''}>{emp.status && String(emp.status).toLowerCase().startsWith('secondment') ? 'Secondment' : (emp.status || '—')}</span></td>
                      <td>{emp.seating_location || '-'}</td>
                      <td>{formatDate(emp.joining_date)}</td>
                      <td>{emp.employment_type || '-'}</td>
                      <td align="right">
                        <button className="btn outline" onClick={() => navigate(`/employees/${emp.employee_id}`)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination-wrap">
                <button type="button" className="pagination-btn pagination-arrow" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1} aria-label="Previous page">←</button>
                {visiblePageNumbers.map(pn => (<button key={pn} type="button" className={`pagination-btn ${page === pn ? 'active' : ''}`} onClick={() => setPage(pn)}>{pn}</button>))}
                <button type="button" className="pagination-btn pagination-arrow" onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages} aria-label="Next page">→</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: '20px',
    background: '#FFFFFF',
    minHeight: '100vh',
    fontFamily: "var(--font-sans)",
    color: '#0F172A',
    animation: 'fadeInUp 0.6s ease-out',
  }
};

const css = `
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; gap: 20px; flex-wrap: wrap; }
.header h2 { margin: 0; font-size: 24px; font-weight: 700; color: #0F172A; font-family: var(--font-sans); }
.subtitle { color: #64748B; font-size: 13px; margin: 2px 0 0 0; }
.card { background: white; padding: 20px; border-radius: 16px; margin-bottom: 16px; box-shadow: var(--shadow-lg); animation: fadeInUp 0.5s ease-out; }
.search-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 20px; flex-wrap: wrap; }
.search-header h3 { margin: 0; font-size: 18px; font-weight: 700; color: #0F172A; font-family: var(--font-sans); }
.search-container { flex: 1; max-width: 360px; min-width: 220px; }
.search-input-wrapper { position: relative; display: flex; align-items: center; }
.search-input { width: 100%; padding: 10px 80px 10px 14px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 14px; font-family: var(--font-sans); background: #F8FAFC; transition: all 0.3s ease; }
.search-input:focus { outline: none; border-color: #1E40AF; background: white; box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1); }
.clear-search-btn { position: absolute; right: 12px; background: #E2E8F0; border: 1px solid #CBD5E1; border-radius: 999px; min-width: 54px; height: 26px; padding: 0 10px; cursor: pointer; font-size: 12px; font-weight: 600; color: #334155; display: inline-flex; align-items: center; justify-content: center; line-height: 1; transition: all 0.2s ease; }
.clear-search-btn:hover { background: #CBD5E1; color: #0F172A; }
.hc-filter-select { padding: 10px 12px; border: 1px solid #CBD5E1; border-radius: 10px; font-size: 14px; font-family: var(--font-sans); background: #F8FAFC; color: #0F172A; cursor: pointer; transition: all 0.2s ease; min-width: 120px; }
.hc-filter-select:focus { outline: none; border-color: #1E40AF; box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1); }
.filter-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 16px; }
.filter-toolbar-left { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.filter-dropdown { position: relative; }
.filter-trigger { min-width: 140px; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 10px 12px; border: 1px solid #CBD5E1; border-radius: 10px; background: #F8FAFC; color: #0F172A; cursor: pointer; font-size: 13px; font-family: var(--font-sans); transition: all 0.2s ease; }
.filter-trigger:hover { border-color: #94A3B8; background: #FFFFFF; }
.filter-trigger.active { border-color: #1D4ED8; background: #EFF6FF; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08); }
.filter-value { flex: 1; text-align: left; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.filter-arrow { color: #64748B; font-size: 12px; }
.filter-menu { position: absolute; top: calc(100% + 6px); left: 0; min-width: 100%; max-height: 240px; overflow-y: auto; background: #FFFFFF; border: 1px solid #CBD5E1; border-radius: 12px; box-shadow: var(--shadow-lg); z-index: var(--z-dropdown); padding: 6px 0; }
.filter-option { width: 100%; border: none; background: transparent; text-align: left; padding: 8px 12px; font-size: 13px; font-family: var(--font-sans); color: #334155; cursor: pointer; transition: background 0.2s ease, color 0.2s ease; }
.filter-option:hover { background: #F1F5F9; }
.filter-option.selected { background: #EFF6FF; color: #1D4ED8; font-weight: 700; }
.filter-menu-group { padding: 8px 12px 2px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #94A3B8; }
.filter-option-indent { padding-left: 24px; }
.filter-reset-btn { white-space: nowrap; }
.btn { padding: 9px 18px; border: none; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.3s ease; display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-sans); }
.btn.outline { background: transparent; color: #1E40AF; border: 2px solid #1E40AF; padding: 7px 14px; font-size: 12px; }
.btn.outline:hover { background: #1E40AF; color: white; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: separate; border-spacing: 0; }
th { padding: 12px 10px; text-align: left; font-weight: 600; color: #0F172A; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #E2E8F0; background: linear-gradient(90deg, #F8FAFC, #F0F4FF); white-space: nowrap; }
td { padding: 10px; border-bottom: 1px solid #E2E8F0; color: #0F172A; font-size: 13px; }
.id-cell { font-size: 12px; color: #64748B; font-weight: 500; white-space: nowrap; }
.name { display: flex; gap: 10px; align-items: center; }
.avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #1E40AF, #0EA5E9); color: white; font-weight: 700; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.muted { color: #94A3B8; font-size: 11px; }
.status-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: capitalize; }
.status-badge.status-active { background: #DCFCE7; color: #15803D; }
.status-badge.status-inactive { background: #FEE2E2; color: #B91C1C; }
.status-badge.status-neutral { background: #E2E8F0; color: #475569; }
.pagination-wrap { display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 20px; flex-wrap: wrap; }
.pagination-btn { width: 40px; height: 40px; border: 1px solid #E2E8F0; background: #FFFFFF; color: #1E293B; font-size: 14px; font-family: var(--font-sans); border-radius: 50%; box-shadow: var(--shadow-sm); cursor: pointer; transition: all 0.2s ease; }
.pagination-btn:hover:not(:disabled) { box-shadow: var(--shadow-md); }
.pagination-btn.active { background: #0F4C81; color: #FFFFFF; border-color: #0F4C81; font-weight: 800; text-decoration: underline; }
.pagination-btn:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: var(--shadow-sm); }
.pagination-arrow { font-size: 24px; line-height: 1; }
.empty-state { text-align: center; padding: 40px 20px; }
.empty-state-icon { font-size: 48px; margin-bottom: 8px; }
.empty-state-text { color: #64748B; font-size: 14px; margin: 0; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
@media (max-width: 768px) {
  .header, .search-header { flex-direction: column; align-items: stretch; }
  .filter-toolbar { align-items: stretch; }
  .filter-toolbar-left { flex-direction: column; }
  .filter-trigger { width: 100%; min-width: 0; }
  .hc-filter-select { width: 100%; }
  .pagination-btn { width: 40px; height: 40px; font-size: 14px; }
  .pagination-arrow { font-size: 24px; }
  th, td { padding: 10px 8px; }
  .name { flex-direction: column; align-items: flex-start; }
}
`;